import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchAllAktienUebersicht,
  fetchAktieAnalyse,
  fetchAktieLive,
  selectAktie,
  selectAktienListe,
  selectAktienListeStatus,
  selectAktieAnalyse,
  selectAktieAnalyseStatus,
  selectAktieLive,
  selectAktieLiveStatus,
} from 'modules/aktien/features/aktienDbSlice';
import { formatKurs, formatProz } from 'core/utils/format';
import { AmpelBadge } from 'core/components/ui';
import ChartAnalyse from 'modules/aktien/components/analyse/ChartAnalyse';
import FundamentalAnalyse from 'modules/aktien/components/analyse/FundamentalAnalyse';
import DividendeAnalyse from 'modules/aktien/components/analyse/DividendeAnalyse';
import RisikoAnalyse from 'modules/aktien/components/analyse/RisikoAnalyse';

const TABS = [
  { key: 'chart', label: 'Chartanalyse' },
  { key: 'fundamental', label: 'Fundamental' },
  { key: 'dividende', label: 'Dividende' },
  { key: 'risiko', label: 'Risiko' },
];

/** Reiter-Button der Analyse-Tabs. */
function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
        active ? 'border-ink text-ink' : 'border-transparent text-ink-muted hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

/** Suchfeld mit Trefferliste (lokaler Zustand, unabhängig von „Liste DB"). */
function AktieSuche({ liste, onSelect }) {
  const [term, setTerm] = useState('');
  const [offen, setOffen] = useState(false);

  const treffer = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t) return [];
    return liste
      .filter(
        (a) =>
          (a.aktienName || '').toLowerCase().includes(t) ||
          (a.isin || '').toLowerCase().includes(t)
      )
      .slice(0, 8);
  }, [liste, term]);

  return (
    <div className="relative max-w-md">
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-muted">🔍</span>
      <input
        type="search"
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          setOffen(true);
        }}
        onFocus={() => setOffen(true)}
        onBlur={() => setTimeout(() => setOffen(false), 150)}
        placeholder="Aktie aus der Datenbank suchen…"
        className="w-full rounded-lg border border-line bg-paper-soft py-2 pl-10 pr-3 text-sm text-ink placeholder-ink-muted outline-none transition-colors focus:border-ink/30 focus:ring-1 focus:ring-ink/20"
      />
      {offen && treffer.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-line bg-paper-soft shadow-lg">
          {treffer.map((a) => (
            <li key={a.isin}>
              <button
                type="button"
                onMouseDown={() => {
                  setTerm('');
                  setOffen(false);
                  onSelect(a.isin);
                }}
                className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-ink/[0.04]"
              >
                <span className="truncate font-medium text-ink">{a.aktienName || a.isin}</span>
                <span className="shrink-0 font-mono text-xs text-ink-muted">{a.isin}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Kompaktes Kursfeld: bevorzugt den Live-Kurs, sonst den Analyse-Stand. */
function KursKopf({ live, liveStatus, analyse }) {
  const istLive = liveStatus === 'succeeded' && live;
  const kurs = istLive ? (live.aktuellPreis ?? live.schlusskurs) : analyse?.stand?.kurs;
  const aenderung = istLive ? live.aenderungProz : null;
  const positiv = (aenderung ?? 0) >= 0;
  const quelle = liveStatus === 'loading' ? 'Live wird geladen…' : istLive ? 'Live · OnVista' : 'gespeichert';

  return (
    <div className="text-right">
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{quelle}</div>
      <div className="font-mono text-2xl font-semibold tabular-nums text-ink">
        {formatKurs(kurs, analyse?.currency)}
      </div>
      {aenderung != null && (
        <div className={`font-mono text-sm tabular-nums ${positiv ? 'text-gain' : 'text-loss'}`}>
          {formatProz(aenderung)}
        </div>
      )}
    </div>
  );
}

/**
 * Reiter „Aktie Analyse": Suche → Auswahl → automatische Analyse (Chart,
 * Fundamental, Dividende, Risiko) inkl. Gesamturteil. Beim Auswählen einer Aktie
 * werden Analyse (aus der DB berechnet) und Live-Kurs frisch geladen.
 */
export default function AktieAnalyseView() {
  const { isin } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const liste = useSelector(selectAktienListe);
  const listeStatus = useSelector(selectAktienListeStatus);
  const analyse = useSelector(selectAktieAnalyse);
  const analyseStatus = useSelector(selectAktieAnalyseStatus);
  const live = useSelector(selectAktieLive);
  const liveStatus = useSelector(selectAktieLiveStatus);

  const [tab, setTab] = useState('chart');

  // Aktienliste (für die Suche) einmalig laden.
  useEffect(() => {
    if (listeStatus === 'idle') {
      dispatch(fetchAllAktienUebersicht());
    }
  }, [dispatch, listeStatus]);

  // Bei Auswahl/Wechsel der ISIN: Analyse + Live-Kurs frisch abrufen.
  useEffect(() => {
    if (isin) {
      dispatch(selectAktie(isin));
      dispatch(fetchAktieAnalyse(isin));
      dispatch(fetchAktieLive(isin));
    }
  }, [dispatch, isin]);

  const handleSelect = (neueIsin) => navigate(`/projekte/aktienanalyse/analyse/${neueIsin}`);

  const name =
    analyse?.aktienName ||
    liste.find((a) => a.isin === isin)?.aktienName ||
    isin;

  return (
    <div>
      <header className="mb-6">
        <h1 className="font-serif text-3xl font-semibold text-ink">Aktie Analyse</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Suche eine Aktie aus der Datenbank – die Analyse wird automatisch aus den
          gespeicherten Kurs- und Geschäftsdaten berechnet.
        </p>
      </header>

      <div className="mb-8">
        <AktieSuche liste={liste} onSelect={handleSelect} />
      </div>

      {!isin && (
        <p className="rounded-xl border border-dashed border-line bg-paper-soft p-10 text-center text-ink-muted">
          Wähle oben eine Aktie aus, um die Analyse zu starten.
        </p>
      )}

      {isin && analyseStatus === 'loading' && (
        <p className="text-ink-muted">Berechne Analyse…</p>
      )}
      {isin && analyseStatus === 'failed' && (
        <p className="rounded-xl border border-dashed border-loss/30 bg-loss/5 p-8 text-center text-loss">
          Die Analyse konnte nicht berechnet werden.
        </p>
      )}

      {isin && analyseStatus === 'succeeded' && analyse && (
        <>
          {/* Kopf: Name + Kursfeld */}
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink">{name}</h2>
              <p className="mt-1 font-mono text-sm text-ink-muted">{analyse.isin}</p>
            </div>
            <KursKopf live={live} liveStatus={liveStatus} analyse={analyse} />
          </div>

          {/* Gesamturteil */}
          {analyse.gesamturteil && (
            <section className="mb-6 rounded-2xl border border-line bg-paper-soft p-6 shadow-sm">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted">
                  Gesamturteil
                </div>
                <AmpelBadge ampel={analyse.gesamturteil.ampel} score={analyse.gesamturteil.score} size="lg" />
              </div>
              <p className="text-sm leading-relaxed text-ink-soft">{analyse.gesamturteil.text}</p>
            </section>
          )}

          {/* Hinweise (z.B. wenig Historie) */}
          {analyse.hinweise && analyse.hinweise.length > 0 && (
            <div className="mb-6 space-y-1">
              {analyse.hinweise.map((h, i) => (
                <p key={i} className="rounded-lg border border-dashed border-line bg-paper-soft px-3 py-2 text-xs text-ink-muted">
                  ⚠️ {h}
                </p>
              ))}
            </div>
          )}

          {/* Tabs */}
          <div className="mb-6 flex gap-1 overflow-x-auto border-b border-line">
            {TABS.map((t) => (
              <TabButton key={t.key} active={tab === t.key} onClick={() => setTab(t.key)}>
                {t.label}
              </TabButton>
            ))}
          </div>

          {tab === 'chart' && <ChartAnalyse chart={analyse.chart} currency={analyse.currency} />}
          {tab === 'fundamental' && (
            <FundamentalAnalyse fundamental={analyse.fundamental} currency={analyse.currency} />
          )}
          {tab === 'dividende' && (
            <DividendeAnalyse dividende={analyse.dividende} currency={analyse.currency} />
          )}
          {tab === 'risiko' && <RisikoAnalyse risiko={analyse.risiko} currency={analyse.currency} />}

          {/* Disclaimer */}
          <p className="mt-8 border-t border-line pt-4 text-center text-xs text-ink-muted">
            {analyse.disclaimer}
          </p>
        </>
      )}
    </div>
  );
}
